import { useCallback, useRef, useState } from "react";
import fileDownload from "js-file-download";
import { extension } from "mime-types";
import { useToaster } from "@design-system/ToastMessages/useToaster";
import { getFilenameFromContentDisposition } from "./http";

export { CSV_MIME, XLSX_MIME } from "@common/constants/mime";

function ensureFilenameExtension(filename: string, mimeType?: string): string {
    const hasExtension = /\.[a-zA-Z0-9]+$/.test(filename);
    if (hasExtension || !mimeType) {
        return filename;
    }
    const ext = extension(mimeType);
    return ext ? `${filename}.${ext}` : filename;
}

interface TsRestBlobResponse {
    // ts-rest infers the contract return type (e.g. StreamableFile) for body,
    // but the blob client returns Blob at runtime
    body: unknown;
    headers: Record<string, string>;
}

/** Save a ts-rest blob response to disk, preferring the server-provided filename from Content-Disposition. */
export function saveBlobResponse(
    response: TsRestBlobResponse,
    fallbackFilename: string,
): void {
    const mimeType = response.headers["content-type"];
    const filename =
        getFilenameFromContentDisposition(
            response.headers["content-disposition"],
        ) ?? ensureFilenameExtension(fallbackFilename, mimeType);
    fileDownload(response.body as Blob, filename, mimeType);
}

function blobFromResponse(response: TsRestBlobResponse): Blob {
    const { body } = response;
    if (!(body instanceof Blob)) {
        throw new Error("Failed to read file");
    }
    return body;
}

export async function readBlobResponseAsText(
    response: TsRestBlobResponse,
): Promise<string> {
    return await blobFromResponse(response).text();
}

export async function readBlobResponseAsArrayBuffer(
    response: TsRestBlobResponse,
): Promise<ArrayBuffer> {
    return await blobFromResponse(response).arrayBuffer();
}

/** Manages downloading state and error handling for file download operations. */
export function useFileDownload<TArgs extends unknown[]>(
    fn: (...args: TArgs) => Promise<void>,
) {
    const [isDownloading, setIsDownloading] = useState(false);
    const { error: showError } = useToaster();
    const fnRef = useRef(fn);
    fnRef.current = fn;
    const downloadingRef = useRef(false);

    const download = useCallback(
        async (...args: TArgs): Promise<boolean> => {
            if (downloadingRef.current) {
                return false;
            }
            downloadingRef.current = true;
            setIsDownloading(true);
            try {
                await fnRef.current(...args);
                return true;
            } catch {
                showError("Failed to download file");
                return false;
            } finally {
                downloadingRef.current = false;
                setIsDownloading(false);
            }
        },
        [showError],
    );

    return { download, isDownloading };
}
